/**
 * relationship-panel.component.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Component, Input, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Modal } from "angular2-modal/plugins/bootstrap";
import { Observable } from "rxjs/Rx";

import { MessagingService } from "../server/messaging.service";
import { ModalHelpers } from "../support/utilities";

import { RelationGraph, RelationNode } from "./relation-graph";
import { RelationGraphModalComponent } from "./relation-graph-modal.component";
import { Relationship, RelationshipType, Role } from "./relationship";
import { Subject } from "./subject";
import { SubjectContextType } from "./subject-context-type";
import { SelectionMode } from "./subject-selector-modal.component";
import { SubjectService } from "./subject.service";

/**
 * The {@link RelationshipPanelComponent} lists the relationships of a
 * particular subject and provides an interface to modify, graph, traverse,
 * create, and delete them.
 */
@Component({
  selector: "relationship-panel",
  template: `
    <div class="panel panel-default">
      <div class="panel-heading" (click)="panelOpen = !panelOpen">
        <h3 class="panel-title">
          Relationships
          <span class="badge">
            {{subject.relationships?.length || 0}}
          </span>
          <button
            class="btn btn-xs btn-default pull-right"
            (click)="$event.stopPropagation(); openNetworkModal()">
            View graph
            <span class="glyphicon glyphicon-blackboard"></span>
          </button>
        </h3>
      </div>
      <table class="table collapse" [class.in]="panelOpen">
        <tbody>
          <tr
            *ngFor="let rel of subject.relationships"
            class="report border-between">

            <!-- self -->
            <td width="50%">
              <label>Role</label>
              <p *ngIf="!rel.editing" class="text-primary">
                {{rel.sourceRole.displayName}}
              </p>
              <select
                *ngIf="rel.editing"
                class="form-control"
                [formControl]="rel.sourceRoleEdit">
                <option
                  *ngFor="let role of rel.getAvailableRoles()"
                  [ngValue]="role">
                  {{role.displayName}}
                </option>
              </select>
              <p>
                <label>Type</label>
                <select
                  class="form-control"
                  disabled
                  title="The type is locked for existing relationships.">
                  <option default>{{rel.type.displayName}}</option>
                </select>
              </p>
              <p>
                <button
                  *ngIf="!rel.editing"
                  class="btn btn-primary form-control"
                  (click)="rel.startEditing()">
                  Change Roles
                </button>
                <button
                  *ngIf="rel.editing"
                  class="btn btn-success form-control"
                  (click)="saveEdits(rel)">
                  Save Changes
                </button>
              </p>
              <p>
                <button
                  *ngIf="rel.editing"
                  class="btn btn-default form-control"
                  (click)="rel.cancelEditing()">
                  Cancel Changes
                </button>
              </p>
              <p>
                <button
                  class="btn btn-danger form-control"
                  (click)="delete(rel)">
                  Delete
                </button>
              </p>
            </td>

            <!-- partner -->
            <td width="50%">
              <label>Partner Role</label>
              <p *ngIf="!rel.editing" class="text-primary">
                {{rel.targetRole.displayName}}
              </p>
              <select
                *ngIf="rel.editing"
                class="form-control"
                [formControl]="rel.targetRoleEdit">
                <option
                  *ngFor="let role of rel.getAvailablePartnerRoles()"
                  [ngValue]="role">
                  {{role.displayName}}
                </option>
              </select>
              <div
                *ngFor="let datum of rel.targetSummary?.summary">
                <label class="capitalized">
                  {{datum.displayName || datum.name}}:
                </label>
                <typed-display [input]="datum"></typed-display>
              </div>
              <button
                class="btn btn-default form-control"
                (click)="traverse(rel)">
                Open Partner
              </button>
            </td>
          </tr>
          <tr *ngIf="!subject.relationships?.length">
            <td colspan="2"><em>No relationships.</em></td>
          </tr>
        </tbody>

        <tfoot>
          <tr>
            <td colspan="2">
              <label>Add Relationship</label>
              <select
                class="form-control"
                [formControl]="newRelationshipType">
                <option
                  *ngFor="let type of subjects.relationshipTypes"
                  [ngValue]="type">
                  {{type.displayName}}
                </option>
              </select>
            </td>
          </tr>
          <tr *ngIf="newRelationship">
            <td width="50%">
              <label>This role</label>
              <select
                class="form-control"
                [formControl]="newRelationship.sourceRoleEdit">
                <option
                  *ngFor="let r of newRelationship.getAvailableRoles()"
                  [ngValue]="r">
                  {{r.displayName}}
                </option>
              </select>
            </td>
            <td width="50%">
              <label>Partner role</label>
              <select
                class="form-control"
                [formControl]="newRelationship.targetRoleEdit">
                <option
                  *ngFor="let r of newRelationship.getAvailablePartnerRoles()"
                  [ngValue]="r">
                  {{r.displayName}}
                </option>
              </select>
            </td>
          </tr>
          <tr *ngIf="newRelationship">
            <td class="center-item text-center">
              <p>
                <label>Partner</label>
                <span
                  *ngIf="!selectedTarget"
                  class="glyphicon glyphicon glyphicon-remove-circle text-danger">
                </span>
                <span
                  *ngIf="selectedTarget"
                  class="glyphicon glyphicon glyphicon-ok-circle text-success">
                </span>
              </p>
              <button
                class="btn btn-xs btn-default"
                (click)="selectTargetFrom('watchlist')">
                Select from Work List
              </button>
              <br>
              <button
                class="btn btn-xs btn-default"
                (click)="selectTargetFrom('search')">
                Find via Search
              </button>
            </td>
            <td class="center-item">
              <span *ngIf="!selectedTarget" class="text-danger">
                «not selected»
              </span>
              <div *ngFor="let datum of selectedTarget?.summary">
                <label class="capitalized">
                  {{datum.displayName || datum.name}}:
                </label>
                <typed-display [input]="datum"></typed-display>
              </div>
            </td>
          </tr>
          <tr *ngIf="newRelationship">
            <td colspan="2">
              <button
                class="btn btn-default form-control"
                (click)="clearNewRelationship()">
                Clear
              </button>
              <button
                class="btn btn-success form-control"
                (click)="create()"
                [disabled]="!selectedTarget || !newRelationship.editsValid">
                Create Relationship
              </button>
            </td>
          </tr>
      </table>
    </div>
`,
  styles: [
    `badge {margin: 10px;}
    .badge-label {font-size: 1.2em;}
    .partial {
      display: inline-block;
      overflow: hidden;
      height: 1em;
      width: 100%;
      text-overflow: ellipsis;
    }
    tfoot {
      border-top: 1px solid #EEE;
    }
    tfoot td:last-of-type {
      width: 500px; /* force full width */
    }
    `,
    ".panel table {width: 100%;}"
  ]
})
export class RelationshipPanelComponent implements OnInit
{
  @Input()
  public subject : Subject;

  @Input()
  public panelOpen : boolean = false;

  public newRelationshipType : FormControl;
  public newRelationship : Relationship;
  public selectedTarget : Subject;

  /**
   * Construct a new {@link RelationshipPanelComponent}.
   */
  constructor (
    private subjects : SubjectService,
    private modal : Modal
  ) {/* DI */}

  /**
   *
   */
  public ngOnInit () : void
  {
    this.newRelationshipType = new FormControl(null, Validators.required);

    this.newRelationshipType.valueChanges.subscribe(
      (type : RelationshipType) => {
        this.selectedTarget = null;
        if (!type)
        {
          this.newRelationship = null;
        }
        else
        {
          this.newRelationship = new Relationship(
            {relationshipType: type.name},
            this.subjects.relationshipTypes,
            this.subjects.roles,
            this.subject
          );
          this.newRelationship.startEditing();
        }
    });
  }

  /**
   * Open a subject context who is partnered with this one via a relationship
   * @param rel The {@link Relationship} to traverse
   */
  public traverse (rel : Relationship) : void
  {
    const partner : Subject = this.subjects.fromReference(rel.buildRef());
    // partner.summary = rel.targetSummary.summary;
    this.subjects.open(partner).subscribe();
  }

  public delete (rel : Relationship) : void
  {
    this.subject.removeRelationship(rel).subscribe();
  }

  public selectTargetFrom (mode : SelectionMode) : void
  {
    this.subjects.selectViaDialog(
      "Choose relationship target...",
      mode,
      this.determinePartnerType()
    ).subscribe((selected : Subject) => {
      if (this.subject.sameAs(selected))
      {
        alert(`You cannot relate this exact subject with itself!
          <br><br>
          (NOTE: There is no such restriction on relating two <em>different</em>
          subjects that happen to represent the same real-world thing.)`);
      }
      else
      {
        this.selectedTarget = selected;
      }
    });
  }

  public determinePartnerType () : SubjectContextType
  {
    if (!this.newRelationship)
    {
      return null;
    }
    if (this.newRelationship.type.sourceType === this.subject.type)
    {
      return this.newRelationship.type.targetType;
    }
    else if (this.newRelationship.type.targetType === this.subject.type)
    {
      return this.newRelationship.type.sourceType;
    }
    else
    {
      console.error(
        "Relationship invalid for subject type!",
        this.newRelationship.type,
        this.subject
      );
      throw new Error("Relationship invalid for subject type!");
    }
  }

  public clearNewRelationship () : void
  {
    this.newRelationshipType.reset();
  }

  public create () : void
  {
    if (!this.newRelationship.sourceRoleEdit.value ||
        !this.newRelationship.targetRoleEdit.value)
    {
      return alert("You are missing required fields.");
    }
    if (!this.selectedTarget)
    {
      return alert("You must select a partner for the relationship!");
    }

    this.subject.relateTo(
      this.selectedTarget,
      this.newRelationship.type,
      this.newRelationship.sourceRoleEdit.value,
      this.newRelationship.targetRoleEdit.value
    ).subscribe((related : boolean) => {
      if (related)
      {
        this.clearNewRelationship();
      }
    });
  }

  public saveEdits (rel : Relationship) : void
  {
    this.subject.updateRelation(
      rel.id,
      rel.sourceRoleEdit.value,
      rel.targetRoleEdit.value
    ).subscribe((saved : boolean) => {
      if (saved)
      {
        rel.cancelEditing();
      }
    });
  }

  private graph : RelationGraph;

  public openNetworkModal () : void
  {
    this.subjects.startRelationshipGraph(this.subject).subscribe(
      (graph : RelationGraph) =>
        ModalHelpers.observeCustom<RelationNode>(
          this.modal,
          RelationGraphModalComponent,
          {graph, size: "lg", dialogClass: "modal-dialog modal-full"}
        ).subscribe((openTarget? : RelationNode) => {
          if (openTarget)
          {
            this.subjects.openRelationGraphNode(
              graph, openTarget);
          }
        }));
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/subjects/relationship-panel.component.ts